import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { createElement } from 'react';
import {
  getCurrentUser,
  signInWithRedirect,
  signOut,
  fetchAuthSession,
  fetchUserAttributes,
  type AuthUser,
} from 'aws-amplify/auth';

export interface UserAttributes {
  email: string;
  given_name: string;
  family_name: string;
}

export interface AuthState {
  user: AuthUser | null;
  userAttributes: UserAttributes | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isSigningOut: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  getJwtToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

/**
 * Decode the payload of a JWT without verification (for extracting claims
 * from the Cognito ID token that we already trust via Amplify).
 */
function decodeJwtPayload(jwt: string): Record<string, unknown> {
  try {
    const payload = jwt.split('.')[1];
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return {};
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [userAttributes, setUserAttributes] = useState<UserAttributes | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const signingOutRef = useRef(false);

  const checkAuth = useCallback(async () => {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);

      let attrs: UserAttributes | null = null;

      // Strategy 1: fetchUserAttributes (calls Cognito UserInfo endpoint)
      try {
        const fetched = await fetchUserAttributes();
        if (fetched.email) {
          attrs = {
            email: fetched.email ?? '',
            given_name: fetched.given_name ?? '',
            family_name: fetched.family_name ?? '',
          };
        }
      } catch {
        // UserInfo endpoint may fail for OAuth flows
      }

      // Strategy 2: decode claims from the ID token directly
      if (!attrs || !attrs.email) {
        try {
          const session = await fetchAuthSession();
          const idToken = session.tokens?.idToken?.toString();
          if (idToken) {
            const claims = decodeJwtPayload(idToken);
            const email = (claims.email as string) || '';
            if (email) {
              attrs = {
                email,
                given_name: (claims.given_name as string) || '',
                family_name: (claims.family_name as string) || '',
              };
            }
          }
        } catch {
          // Token decode failed
        }
      }

      setUserAttributes(attrs);
    } catch {
      setUser(null);
      setUserAttributes(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleSignIn = useCallback(async () => {
    if (signingOutRef.current) return;
    try {
      await signInWithRedirect();
    } catch (err) {
      console.error('Sign in failed:', err);
    }
  }, []);

  const handleSignOut = useCallback(async () => {
    try {
      signingOutRef.current = true;
      setIsSigningOut(true);
      await signOut({ global: true });
      setUser(null);
      setUserAttributes(null);
    } catch (err) {
      console.error('Sign out failed:', err);
      signingOutRef.current = false;
      setIsSigningOut(false);
    }
  }, []);

  const getJwtToken = useCallback(async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() ?? null;
    } catch {
      return null;
    }
  }, []);

  const value: AuthState = {
    user,
    userAttributes,
    isAuthenticated: user !== null,
    isLoading,
    isSigningOut,
    signIn: handleSignIn,
    signOut: handleSignOut,
    getJwtToken,
  };

  return createElement(AuthContext.Provider, { value }, children);
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}

export { AuthContext };
